/* eslint-disable @typescript-eslint/ban-types */
import QueryTags from '../../types/QueryTags'
import { EndpointBuilder } from '@reduxjs/toolkit/dist/query/endpointDefinitions'
import { BaseQueryFn, FetchArgs, FetchBaseQueryError, FetchBaseQueryMeta } from '@reduxjs/toolkit/query/react'
import { FetchCardsRequest, FetchCardsResponse, FetchMiniStatementRequest, FetchMiniStatementResponse, LockCardRequest, LockCardResponse, PinRetriesRequest, PinRetriesResponse, SetLimitRequest, SetLimitResponse, ReloadPrepaidRequest, ReloadPrepaidResponse, FetchCardInfoRequest, FetchCardInfoResponse, GetCreditLimitRequest, GetCreditLimitResponse, CreateVirtualCardResponse, CreateVirtualCardRequest, GetVirtualCardsDetailsResponse, GetVirtualCardSensitiveDataRequest, GetVirtualCardSensitiveDataResponse, GetVirtualCardCreationLimitResponse, CheckVirtualCardCreationPermissionResponse } from './types'


const basePath = 'card-service'


const cardsEndpoints = (builder: EndpointBuilder<
    BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError, {}, FetchBaseQueryMeta>, QueryTags, 'data'>) => 
  ({
    getCards: builder.query<FetchCardsResponse, FetchCardsRequest> ({
      query: ({ customerIdentityId }) => {
        return `${basePath}/api/customers/${customerIdentityId}/cards`
      },
      providesTags: ['cards']
    }),
    cardMiniStatement: builder.query<FetchMiniStatementResponse[], FetchMiniStatementRequest>({
      query: ({ customerIdentityId, cardUId, maskedCardNumber, mbr }) => ({
        url: `${basePath}/api/customers/${customerIdentityId}/cards/${cardUId}/mini-statement`,
        method: 'POST',
        params: { maskedCardNumber, mbr },
      }),
    }),
    lockCard: builder.mutation<LockCardResponse[], LockCardRequest> ({
      query: ({ customerIdentityId, cardUId, maskedCardNumber, cardStatus, reason }) => {
        return ({
          url:`${basePath}/api/customers/${customerIdentityId}/cards/${cardUId}/change-card-status`,
          method: 'PUT',
          params: { maskedCardNumber, cardStatus },
          body: {reason}
        })
      },
      invalidatesTags: ['cards'],
    }),
    pinResetRetries: builder.mutation<PinRetriesResponse[], PinRetriesRequest> ({
      query: ({ customerIdentityId, cardUId }) => {
        return ({
          url:`${basePath}/api/customers/${customerIdentityId}/cards/${cardUId}/reset-pin-retries`,
          method: 'PUT'
        })
      } 
    }),
    setLimit: builder.mutation<SetLimitResponse[], SetLimitRequest> ({
      query: ({ customerIdentityId, cardUId, limitType, amount, currency }) => {
        return ({
          url:`${basePath}/api/customers/${customerIdentityId}/cards/${cardUId}/set-limit`,
          method: 'PUT',
          body: {limitType, amount, currency}
        })
      },
      invalidatesTags: ['cardsLimit'],
    }),
    reloadPrepaid: builder.mutation<ReloadPrepaidResponse[], ReloadPrepaidRequest> ({
      query: ({ customerIdentityId, cardUId, amount, customerAccountNumber, customerAccountNumberCurrency, description }) => {
        return ({
          url:`${basePath}/api/customers/${customerIdentityId}/reload-prepaid-card`,
          method: 'POST',
          body: {
            cardUId,
            amount,
            customerAccountNumber,
            customerAccountNumberCurrency,
            description,
          }
        })
      }
    }),
    getCardInfo: builder.query<FetchCardInfoResponse, FetchCardInfoRequest> ({
      query: ({ digitalProfileId, cardUId, maskedCardNumber }) => {
        return ({
          url:`${basePath}/api/customers/${digitalProfileId}/cards/${cardUId}`,
          method: 'GET',
          params: { maskedCardNumber },
        })
      } 
    }),
    getCreditLimitInfo: builder.query<GetCreditLimitResponse, GetCreditLimitRequest> ({
      query: ({ digitalProfileId, cardUId, maskedCardNumber }) => {
        return ({
          url:`${basePath}/api/customers/${digitalProfileId}/cards/${cardUId}/credit-card-limit-details`,
          method: 'GET',
          params: { maskedCardNumber },
        })
      },
      providesTags: ['cardsLimit'],
    }),
    getVirtualCardsDetails: builder.query<GetVirtualCardsDetailsResponse, void> ({
      query: () => {
        return ({
          url:`${basePath}/api/virtual/virtual-card-details`,
          method: 'GET',
        })
      } 
    }),
    createVirtualCards: builder.mutation<CreateVirtualCardResponse, CreateVirtualCardRequest> ({
      query: ({ digitalProfileId, ...body }) => {
        return ({
          url:`${basePath}/api/virtual/${digitalProfileId}/create-virtual-card`,
          method: 'POST',
          body,
        })
      },
      invalidatesTags: ['cards']
    }),
    getVirtualCardSensitiveData: builder.query<GetVirtualCardSensitiveDataResponse, GetVirtualCardSensitiveDataRequest> ({
      query: ({ digitalProfileId, cardUID }) => {
        return ({
          url:`${basePath}/api/virtual/${digitalProfileId}/virtual-card-sensitive-data/${cardUID}`,
          method: 'GET',
        })
      } 
    }),
    getVirtualCardCreationLimit: builder.query<GetVirtualCardCreationLimitResponse, { digitalProfileId: string, }> ({
      query: ({ digitalProfileId }) => {
        return ({
          url:`${basePath}/api/virtual/${digitalProfileId}/virtual-card-creation-limit`,
          method: 'GET',
        })
      } 
    }),
    checkVirtualCardCreationPermission: builder.query<CheckVirtualCardCreationPermissionResponse, { digitalProfileId: string, }> ({
      query: ({ digitalProfileId }) => {
        return ({
          url:`${basePath}/api/virtual/${digitalProfileId}/create-virtual-card-permission`,
          method: 'GET',
        })
      } 
    }),
  })
  
export default cardsEndpoints